import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { getPatientQueryOptions } from '@/api/queries/patients';
import { FullPatient } from '@/api/schema/patient';
import { getProcedureQueryOptions } from '@/api/queries/procedures';
import { ErrorComponent } from '@/components/generalError';
import { Images } from '@/components/procedureDetails/images/images';
import { Planning } from '@/components/procedureDetails/planning/planning';
import { Procedure } from '@/components/procedureDetails/procedure/procedure';
import { ProcedureHeader } from '@/components/procedures/procedureHeader';
import { BackButton } from '@/components/ui/backButton';
import { ProceduresContext, QueryStateKey } from '@/context/proceduresContext';
import { Tabs } from '@/components/ui/tabs';
import { usePermission } from '@/hooks/usePermission';
import { routes } from '@/types/routes';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { t } from 'i18next';
import { parseAsNumberLiteral, useQueryState } from 'nuqs';

export const Route = createFileRoute('/_app/procedures/$procedureId/')({
  loader: ({ context: { queryClient }, params: { procedureId } }) => {
    return queryClient.ensureQueryData(getProcedureQueryOptions(procedureId));
  },
  errorComponent: ErrorComponent,
  component: RouteComponent,
});

export const TAB_INDEXES = {
  IMAGES: 0,
  PLANNING: 1,
  PROCEDURE: 2,
};

const useTabs = ({ patient }: { patient: FullPatient }) => {
  const tabs = [
    {
      index: TAB_INDEXES.IMAGES,
      name: t('tabs.images', { ns: 'procedures' }),
      component: <Images patient={ patient } />,
    },
    {
      index: TAB_INDEXES.PLANNING,
      name: t('tabs.planning', { ns: 'procedures' }),
      component: <Planning />,
    },
    {
      index: TAB_INDEXES.PROCEDURE,
      name: t('tabs.procedure', { ns: 'procedures' }),
      component: <Procedure />,
    },
  ];

  return tabs;
};

function RouteComponent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = Route.useParams();
  const { page: proceduresListPage } = useContext(ProceduresContext);
  const { procedureId } = params as { procedureId: string };
  const { data: procedure } = useSuspenseQuery(
    getProcedureQueryOptions(procedureId),
  );
  const { data: patient } = useSuspenseQuery(
    getPatientQueryOptions(procedure.patient.id),
  );
  const tabs = useTabs({ patient: patient as FullPatient });
  const allowedTabs = tabs.map(tab => tab.index);
  const [ activeTab, setActiveTab ] = useQueryState(
    'tab',
    parseAsNumberLiteral(allowedTabs).withDefault(TAB_INDEXES.IMAGES),
  );

  const hasPermissions = usePermission('procedure.canRead', {
    siteId: patient.site.id,
  });
  if (!hasPermissions) {
    void navigate({ to: routes.patients, replace: true });
    return null;
  }

  return (
    <section className="size-full grid grid-rows-[auto_1fr] gap-space-lg">
      <header className="flex flex-col space-y-space-2xs overflow-hidden">
        <BackButton
          linkLabel={ t('routes.procedureList') }
          href={ `${routes.procedures}?${QueryStateKey}=${proceduresListPage}` }
        />
        <ProcedureHeader procedure={ procedure } patient={ patient } />
      </header>
      <Tabs
        tabs={ tabs }
        activeTab={ activeTab }
        setActiveTab={ setActiveTab }
        className="grid grid-rows-[auto_auto_1fr] gap-space-lg [&]:space-y-0"
      />
    </section>
  );
}
